import React from 'react';
import styled from '@emotion/styled';
import { CheckCircle, AlertTriangle, XCircle, AlertCircle } from 'lucide-react';

const MatchStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  min-height: 120px;
`;

const MatchStatusIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  border: 2px solid;
  
  ${props => {
    switch(props.variant) {
      case 'perfect':
        return `
          background: #dcfce7;
          border-color: #22c55e;
          color: #16a34a;
        `;
      case 'matched':
        return `
          background: #dcfce7;
          border-color: #22c55e;
          color: #16a34a;
        `;
      case 'maybe':
        return `
          background: #fef3c7;
          border-color: #f59e0b;
          color: #d97706;
        `;
      case 'mismatch':
        return `
          background: #fef2f2;
          border-color: #ef4444;
          color: #dc2626;
        `;
      case 'no_data':
        return `
          background: #d1fae5;
          border-color: #6ee7b7;
          color: #047857;
        `;
      default:
        return `
          background: #f3f4f6;
          border-color: #9ca3af;
          color: #6b7280;
        `;
    }
  }}
  
  ${props => props.clickable && `
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  `}
`;

const MatchLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MatchStatusIndicator = ({ 
  status, 
  isPerfectMatch = false, 
  onStatusChange 
}) => {
  // Allow clicking in all states except explicit no_data
  const isClickable = status !== 'no_data';
  
  const getMatchLabel = (status) => {
    switch(status) {
      case 'perfect':
        return 'Perfect Match';
      case 'matched':
        return 'Matched';
      case 'maybe':
        return 'Maybe';
      case 'mismatch':
        return 'Mismatch';
      case 'no_data':
        return 'No Data';
      default:
        return 'Unknown';
    }
  };

  const getMatchStatusIcon = (status) => {
    switch(status) {
      case 'perfect':
        return <CheckCircle size={24} />;
      case 'matched':
        return <CheckCircle size={24} />;
      case 'maybe':
        return <AlertTriangle size={24} />;
      case 'mismatch':
        return <XCircle size={24} />;
      case 'no_data':
        return <AlertCircle size={24} />;
      default:
        return <AlertTriangle size={24} />;
    }
  };
  
  const handleClick = () => {
    if (!isClickable || !onStatusChange) {
      return;
    }
    
    // Cycle through full set including perfect
    // mismatch -> maybe -> matched -> perfect -> mismatch
    let nextStatus;
    switch(status) {
      case 'mismatch':
        nextStatus = 'maybe';
        break;
      case 'maybe':
        nextStatus = 'matched';
        break;
      case 'matched':
        nextStatus = 'perfect';
        break;
      case 'perfect':
        nextStatus = 'mismatch';
        break;
      default:
        nextStatus = 'mismatch';
    }
    onStatusChange(nextStatus);
  };
  
  return (
    <MatchStatusContainer>
      <MatchStatusIcon 
        variant={status} 
        clickable={isClickable}
        onClick={handleClick}
        data-cy="match-status-toggle"
        title={isClickable ? `Click to change status. Current: ${getMatchLabel(status)}` : 'No data available - cannot be changed'}
      >
        {getMatchStatusIcon(status)}
      </MatchStatusIcon>
      <MatchLabel data-cy="match-status-label">{getMatchLabel(status)}</MatchLabel>
      {isClickable && (
        <div 
          style={{ 
            fontSize: '0.65rem', 
            color: '#9ca3af', 
            marginTop: '0.25rem',
            textAlign: 'center',
            cursor: 'pointer'
          }}
          onClick={handleClick}
          title={`Click to change status. Current: ${getMatchLabel(status)}`}
        >
          Click to cycle
        </div>
      )}
    </MatchStatusContainer>
  );
};

export default MatchStatusIndicator;
